import React, { useState, useEffect, useCallback } from 'react'
import { Lock, Eye, EyeOff, AlertTriangle, RefreshCw } from 'lucide-react'
import Swal from 'sweetalert2'
import _ from 'lodash'
import './_sensitive-file-input.scss'
import {
  defaultDeniedConfig,
  defaultProtectedConfig,
  defaultReasonConfig,
  ISensitiveFileInputProps,
  AUTO_HIDE_SECONDS,
  IAccessReasonConfig,
  IAccessDeniedConfig,
  IProtectedViewConfig,
} from 'shared-libs/components/types/sensitive-file'
import apis from 'browser/app/models/apis'
import { getCurrentGeoPosition } from 'browser/app/utils/utils'

/**
 * Renders a protected viewer for sensitive files (e.g. PII documents).
 * Access is gated behind an explicit user action and requires a reason,
 * which is logged for audit purposes.
 *
 * When revealed, the file is shown temporarily and automatically
 * re-hidden after a short timeout to reduce accidental exposure.
 *
 * TODO: Currently supports viewing only.
 * - Add support for uploading sensitive files on web in the future.
 */
export const SensitiveFileInput: React.FC<ISensitiveFileInputProps> = ({
  label = 'Driver License Verification',
  emptyText = 'Not provided',
  value,
  frames,
  protectedCfg = defaultProtectedConfig,
  reasonCfg = defaultReasonConfig,
  deniedCfg = defaultDeniedConfig,
}) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(AUTO_HIDE_SECONDS)

  /* ------------------ helpers ------------------ */

  const requestSignedUrl = async (reason: string): Promise<any> => {
    if (!frames || !value?.storage) return null

    const entity = frames.getValue('entity')
    const valuePath = (frames.getContext('valuePath') ?? []).join('.')

    // best effort
    let geolocation = {}
    try {
      const position = await getCurrentGeoPosition()
      geolocation = position.coords
    } catch (error: any) {
      console.warn('Unable to get geolocation', error)
    }

    return await apis.requestSensitiveFileAccess({
      entityId: entity.uniqueId,
      fileId: value.storage.fileId,
      provider: value.storage.provider,
      retentionAge: value.storage.retentionAge,
      fieldPath: valuePath,
      geolocation: geolocation,
      reason,
    })
  }

  /* ------------------ auto-hide timer ------------------ */

  useEffect(() => {
    if (!signedUrl) return

    setSecondsLeft(AUTO_HIDE_SECONDS)

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          setSignedUrl(null)
          setError(null)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [signedUrl])

  /* ------------------ actions ------------------ */

  const handleViewClick = useCallback(async () => {
    setError(null)
    const props: IAccessReasonConfig = { ...defaultReasonConfig, ...reasonCfg }
    const result = await Swal.fire({
      didOpen: () => {
        const container = Swal.getContainer()
        container?.setAttribute?.('data-private', 'true')
      },
      title: props?.title,
      text: props.text,
      icon: 'warning',
      input: 'textarea',
      inputPlaceholder: props.placeholder,
      showCancelButton: true,
      confirmButtonText: props?.confirmText,
      reverseButtons: true,
      backdrop: false,
      inputValidator: (value) => (!value ? 'You need to write a reason!' : undefined),
      buttonsStyling: false,

      customClass: {
        confirmButton: 'c-sensitive-btn is-primary',
        cancelButton: 'c-sensitive-btn is-secondary',
        // input and popup overwrite
        // input: 'c-sensitive-input',
        // popup: 'c-sensitive-popup-override'
      },
    })

    if (!result.isConfirmed || !result.value) return

    try {
      const response = await requestSignedUrl(result.value)
      setSignedUrl(response?.url)
      setError(null)
    } catch (error: any) {
      console.error('Failed to get signed URL:', error)

      const message = error?.message || error?.details || 'Invalid permission'
      setError(message)
      setSignedUrl(null)
    }
  }, [frames, value])

  const handleManualHide = () => {
    setError(null)
    setSignedUrl(null)
  }

  /* ------------------ render ------------------ */

  const renderErrorView = () => {
    const props: IAccessDeniedConfig = { ...defaultDeniedConfig, ...deniedCfg }
    return (
      <div className="c-sensitive-protected-overlay c-sensitive-error-container">
        <div className="c-sensitive-error-icon">
          <AlertTriangle size={24} />
        </div>
        <div className="c-sensitive-protected-text">
          <h4>{props?.title}</h4>
          <p>{error}</p>
        </div>
        <div className="c-sensitive-error-actions">
          <button type="button" onClick={handleManualHide} className="c-sensitive-btn is-secondary">
            {props.cancelText}
          </button>
          <button type="button" onClick={handleViewClick} className="c-sensitive-btn is-primary">
            <RefreshCw size={14} />
            {props.retryText}
          </button>
        </div>
      </div>
    )
  }

  const renderPreviewView = () => {
    return (
      <div className="c-sensitive-image-wrapper">
        <img src={signedUrl} alt="Secure Document" className="c-sensitive-image" />

        <button type="button" onClick={handleManualHide} className="c-sensitive-btn-hide">
          <EyeOff size={14} /> Auto-blur in ({secondsLeft})
        </button>
      </div>
    )
  }

  const renderProtectedView = () => {
    const props: IProtectedViewConfig = { ...defaultProtectedConfig, ...protectedCfg }
    return (
      <div className="c-sensitive-protected-overlay">
        <div className="c-sensitive-lock-icon">
          <Lock size={24} />
        </div>
        <div className="c-sensitive-protected-text">
          <h4>{props.title}</h4>
          <p>{props.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={handleViewClick}
          className="c-sensitive-btn is-primary is-pill"
        >
          <Eye size={16} />
          {props.submitText}
        </button>
      </div>
    )
  }

  const renderEmpty = () => (
    <div className="c-sensitive-viewer-box c-sensitive-empty">
      <span className="c-sensitive-empty-text">{emptyText}</span>
    </div>
  )

  const renderViewer = () => (
    <div className="c-sensitive-viewer-box">
      {error ? renderErrorView() : signedUrl ? renderPreviewView() : renderProtectedView()}
      {/* Hide the footer note if there is an error to reduce noise */}
      {!signedUrl && !error && (
        <div className="c-sensitive-footer-row">
          <Lock size={12} />
          <span>All access events are logged in the Activity feed</span>
        </div>
      )}
    </div>
  )

  const hasData =
    !_.isEmpty(value?.storage?.fileId) &&
    !_.isEmpty(value?.storage?.provider) &&
    !_.isEmpty(value?.storage?.retentionAge)

  return (
    <div data-private className="c-sensitive-form-row">
      <label className="c-sensitive-label">{label}</label>
      {hasData ? renderViewer() : renderEmpty()}
    </div>
  )
}
